// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
//
// The semantic palette, in one place, so colour cannot drift between views.
//
// THERE IS NO RED AND NO GREEN ON THIS SITE, no traffic light and no diverging
// ramp. Colour must never score a legal finding. "Native title does not exist"
// is a court's finding about a particular area, not a failure, and painting it
// red would editorialise a judgment in the legend.
//
// TWO HUES, TWO TEXTURES — hue is never load-bearing on its own:
//
//   exclusive      deep ochre,  solid     ─┐ both are recognition, so they
//   non-exclusive  light ochre, stippled  ─┘ share ONE hue and separate by
//                                            luminance and texture
//   does not exist slate,       solid     ─┐ an equally real finding, at equal
//   extinguished   slate,       hatched   ─┘ visual weight — never faded out
//
// The four always appear in the same fixed order, so position is a third
// redundant channel. A reader with deuteranopia or protanopia, or looking at a
// greyscale print, can separate all four from luminance and pattern alone.

export const TENURE_ORDER = ['exclusive', 'nonexclusive', 'notexist', 'extinguished'] as const;
export type Tenure = (typeof TENURE_ORDER)[number];

export interface TenureStyle {
  id: Tenure;
  /** The register's own phrasing. Never paraphrased in a legend or a tooltip. */
  register: string;
  /** A short label for axes and legends. */
  label: string;
  /** One sentence a reader who has never met the term can understand. */
  plain: string;
  fill: string;
  /** The SVG pattern id that carries the redundant texture, or null for solid. */
  texture: 'stipple' | 'hatch' | null;
}

export const TENURE: Record<Tenure, TenureStyle> = {
  exclusive: {
    id: 'exclusive',
    register: 'Native title exists (exclusive)',
    label: 'Exclusive possession',
    plain: 'The native title holders have the right to possess this land to the exclusion of everyone else — the strongest form the law recognises.',
    fill: '#9A5B1B',
    texture: null,
  },
  nonexclusive: {
    id: 'nonexclusive',
    register: 'Native title exists (non-exclusive)',
    label: 'Non-exclusive rights',
    plain: 'Native title rights exist here — to hunt, fish, camp, hold ceremony, take resources — but they coexist with someone else’s interest in the same land, such as a pastoral lease.',
    fill: '#D9A05B',
    texture: 'stipple',
  },
  notexist: {
    id: 'notexist',
    register: 'Native title does not exist',
    label: 'Does not exist',
    plain: 'The court found that native title does not exist over this area. It is a finding about this ground, not about the people who asked.',
    fill: '#6B7280',
    texture: null,
  },
  extinguished: {
    id: 'extinguished',
    register: 'Native title extinguished',
    label: 'Extinguished',
    plain: 'Native title once existed here but has been extinguished by a previous act of government — a freehold grant, a public work — and cannot be revived.',
    fill: '#9AA3AF',
    texture: 'hatch',
  },
};

/** The two classes that mean native title WAS recognised over that ground. */
export const RECOGNISED: Tenure[] = ['exclusive', 'nonexclusive'];
export const isRecognised = (t: Tenure) => RECOGNISED.includes(t);

/**
 * The determination-level findings — a DIFFERENT vocabulary from the tenure
 * classes above, and the site never lets them blur. A determination is one
 * court decision; the tenure classes describe pieces of ground inside it.
 */
export const FINDING_ORDER = ['entire', 'parts', 'none', 'compensation'] as const;
export type Finding = (typeof FINDING_ORDER)[number];

export const FINDING: Record<Finding, { label: string; register: string; plain: string; fill: string }> = {
  entire: {
    label: 'Exists over the whole area',
    register: 'Native title exists in the entire determination area',
    plain: 'The court found native title exists across everything it was asked about.',
    fill: '#9A5B1B',
  },
  parts: {
    label: 'Exists in parts',
    register: 'Native title exists in parts of the determination area',
    plain: 'The court found native title exists over some of the area and not over the rest. Most determinations are like this.',
    fill: '#D9A05B',
  },
  none: {
    label: 'Does not exist',
    register: 'Native title does not exist',
    plain: 'The court found native title does not exist anywhere in the area it considered.',
    fill: '#6B7280',
  },
  compensation: {
    label: 'Compensation payable',
    register: 'Compensation is payable',
    plain: 'The matter decided compensation for native title that was impaired or extinguished, rather than dividing the ground into categories. There are only four of these.',
    fill: '#3F3A34',
  },
};

/**
 * METHOD IS A STROKE, NEVER A HUE.
 *
 * How a determination was reached composes with what it found, so it has to be
 * readable on top of the outcome fill. Keeping it out of the colour channel
 * also makes it structurally impossible to cross-tabulate method against
 * outcome by colour, which is how a "litigation success rate" gets drawn by
 * accident — a framing this site does not make.
 */
export const METHOD_ORDER = ['consent', 'unopposed', 'litigated'] as const;
export type Method = (typeof METHOD_ORDER)[number];

export const METHOD: Record<Method, { label: string; plain: string; dash: string; width: number }> = {
  consent: {
    label: 'Consent',
    plain: 'The parties — the claim group, the state, pastoralists, miners, councils — negotiated an agreement and asked the Court to make orders reflecting it.',
    dash: '',
    width: 0,
  },
  unopposed: {
    label: 'Unopposed',
    plain: 'Nobody opposed the application, so the Court made the determination without a contest.',
    dash: '2 2',
    width: 1,
  },
  litigated: {
    label: 'Litigated',
    plain: 'The matter was argued out and decided by a judge after a trial.',
    dash: '',
    width: 1.8,
  },
};

/** Live matters are never filled. An assertion under consideration must not read as a finding. */
export const PENDING_STROKE = '#2F6F7E';

/** "Not recorded" is always its own third state — never folded into "no", never zero-filled. */
export const UNRECORDED = '#B9B2A6';

/**
 * The agreement subject vocabulary gets its own categorical family, used only
 * on the agreements view, so a subject tag can never be mistaken for an
 * outcome category anywhere else on the site.
 */
export const SUBJECT_COLOURS = [
  '#3F6B5B', '#7A5C33', '#4A5C7A', '#8A6A8C', '#2F6F7E', '#8C5A4A',
  '#5B6E3F', '#6E5B8A', '#A07A3F', '#4F7A6E', '#7A4F5B', '#5A6B7A',
];

export function subjectColour(i: number): string {
  return SUBJECT_COLOURS[i % SUBJECT_COLOURS.length];
}

/**
 * The scope sentence.
 *
 * Shown in the footer of every view, in these words, every time. Three things
 * in it are not negotiable: the boundaries are indicative, nothing here is
 * legal advice, and a determination is not automatically a recognition.
 */
export const SCOPE = 'Seven public registers of the National Native Title Tribunal, published on data.gov.au under CC BY 4.0 and refreshed daily. The boundaries shown are INDICATIVE ONLY — they are not cadastral or legal boundaries, they cannot be used to establish rights over any particular parcel of land or water, and nothing here is legal advice. Every area figure is a geometric union measured in an equal-area projection, never a sum of the register’s per-record areas, and covers land only. A determination is a court’s finding about a specific area at a specific time, and a determination is not automatically a recognition.';

export const SCOPE_SHORT = 'National Native Title Tribunal registers · indicative boundaries, not legal advice · areas are dissolved unions, land only';

/** The caveat without which the map is read wrongly. */
export const ALRA_CAVEAT = 'The Northern Territory’s share looks low for a reason that is not on this register: roughly half the Territory is already Aboriginal freehold under the Aboriginal Land Rights (Northern Territory) Act 1976 — a different statute, granting a different and generally stronger form of title, which the native title registers do not cover. The figure here is not a measure of Aboriginal land in the Territory.';

export function mixHex(a: string, b: string, k: number): string {
  const pa = parseHex(a);
  const pb = parseHex(b);
  if (!pa || !pb) return a;
  const c = (i: 0 | 1 | 2) => Math.round(pa[i] + (pb[i] - pa[i]) * Math.max(0, Math.min(1, k)));
  return `#${[c(0), c(1), c(2)].map((n) => n.toString(16).padStart(2, '0')).join('')}`;
}

function parseHex(h: string): [number, number, number] | null {
  const m = /^#?([0-9a-f]{6})$/i.exec(h.trim());
  if (!m) return null;
  const n = parseInt(m[1], 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}
